import { Layer, Line, Rect, Stage } from 'react-konva';
import React, { Component, PureComponent } from 'react';
import { FrenzyLine, FrenzyFilledPolygon, PixelField, Point, Direction, Axis, FrenzyPolygon } from './Model';
import { FrenzyColor } from './FrenzyConstants';
import { sparseArrayToPointsArray, toOptimalPolyArray } from './utils/SparseArray';
import { describeLine, describeLines, normaliseToRight, points, vectorizeIntoHorizontalLines } from './utils/Line';
import { describePolygons, isInsideComplexPolygon, isPointAnywhereOnPolygonLines, mostBottomRightActualPointForPolygon, topmostPointForPolygon, truncatePolygonAt } from './utils/Polygon';
import { pointsToLine } from './Utils';
import { visitEachPointOnLine } from './model/LinePointVisitor';

type Props = {
	scale: number;
	width: number;
	height: number; 
	borderLines: FrenzyLine[];
	completedLines: FrenzyLine[];
	activeLines: FrenzyLine[];
	filledPolygons: FrenzyFilledPolygon[];
	partiallyFilledArea?: PixelField;
	drainAnimationRow:  number;
	onRowDrained?: () => void;
	onDrainAnimationComplete?: () => void;
};

class Display extends PureComponent<Props> {

	static renderFilledPolygon = (scale: number) => (filledPolygon: FrenzyFilledPolygon, index: number) => {
		const { polygonLines, color } = filledPolygon;
		return renderFilledArea(`filled-${index}`, polygonLines, FrenzyColor.CYAN, color);
	}



	static renderPartialPolygon = (p: FrenzyFilledPolygon, index: number, drainAnimationRow: number, allPolygons: FrenzyPolygon[]) => {
		const { polygonLines, color } = p;

		// Draw the whole thing with the borders in place and a black background,
		// then draw the part of the polygon BELOW the drain row, with no stroke,
		// in the correct color

		const choppedPolygonLines = truncatePolygonAt(polygonLines, drainAnimationRow);

		// console.log(`chops at ${drainAnimationRow}: ${describeLines(choppedPolygonLines)}`)
		const renderBlottingLine = renderLine(FrenzyColor.BLACK)

		// Any line which is ON the drain row should be rendered in black LAST to cover the cyan 
		const blottingLines = choppedPolygonLines.filter((l) => 
			l.direction.axis === Axis.HORIZONTAL && l.start[1] === drainAnimationRow
		)

		// const legalPoints: Array<Point> = []; 

		// hLines.forEach((l) => {
		// 	const lToR = normaliseToRight(l);
		// 	visitEachPointOnLine(lToR, lToR.start, (p) => {
		// 		if (isInsideComplexPolygon(p[0], p[1], polygonLines)) {
		// 			legalPoints.push(p);
		// 		}
		// 	})
		// })

		// const blottingLines = vectorizeIntoLines(legalPoints)

		// if (blottingLines.length > 1) {
		// 	console.log(`blottingLines: ${JSON.stringify(blottingLines, null, 2)}`)

		// }

		return (
			<React.Fragment key={`pp-${index}`}>
				{ renderFilledArea(`${index}-back`, polygonLines, FrenzyColor.CYAN, FrenzyColor.BLACK) }
				{ renderFilledArea(`${index}-front`, choppedPolygonLines,FrenzyColor.CYAN, color) }
				{ blottingLines.map((l,i) => renderBlottingLine(l, (index * 100) + i))}
			</React.Fragment>
		); 
	}

	static filterForBlackedOutPolygons = (filledPolygons: FrenzyFilledPolygon[], drainAnimationRow: number) => {
		return filledPolygons.filter(p => {
			const [, maxY] = mostBottomRightActualPointForPolygon(p);
			return maxY <= drainAnimationRow;
		});
	}

	static filterForPartialPolygons = (filledPolygons: FrenzyFilledPolygon[], drainAnimationRow: number) => {
		return filledPolygons.filter(p => {
			const minY = topmostPointForPolygon(p);
			if (minY > drainAnimationRow) return false;
			const [, maxY] = mostBottomRightActualPointForPolygon(p);
			return maxY > drainAnimationRow;
		});
	}

	static filterForUnaffectedPolygons = (filledPolygons: FrenzyFilledPolygon[], drainAnimationRow: number) => {
		return filledPolygons.filter(p => {
			const minY = topmostPointForPolygon(p);
			return minY > drainAnimationRow;
		});
	}

	static renderDrainingAnimation = (filledPolygons: FrenzyFilledPolygon[], drainAnimationRow: number) => {
		const blackedOutPolygons = Display.filterForBlackedOutPolygons(filledPolygons, drainAnimationRow);
		const partialPolygons = Display.filterForPartialPolygons(filledPolygons, drainAnimationRow);
		const unaffectedPolygons = Display.filterForUnaffectedPolygons(filledPolygons, drainAnimationRow);
		return (
			<React.Fragment>
			{ blackedOutPolygons.map((p, i) => renderFilledArea(`black${i}`, p.polygonLines, FrenzyColor.CYAN, FrenzyColor.BLACK)) }
			{ partialPolygons.map((p, i) => Display.renderPartialPolygon(p, i, drainAnimationRow, filledPolygons)) }
			{ unaffectedPolygons.map((p, i) => renderFilledArea(`unaffected${i}`, p.polygonLines, FrenzyColor.CYAN, p.color)) }
			</React.Fragment>
		);	
	}

	render() {
		const { scale, width, height, activeLines, borderLines, completedLines, filledPolygons, partiallyFilledArea, drainAnimationRow } = this.props;
		const renderFilledPolyAtScale = Display.renderFilledPolygon(scale);

		return (
			<Stage listening={false} draggable={false} width={(width * scale) + 2} height={scale * height}>
				<Layer x={1} y={1} listening={false} draggable={false} scaleX={scale} scaleY={scale}>
					{ completedLines.map(renderLine(FrenzyColor.CYAN)) }
					{ activeLines.map(renderLine(FrenzyColor.GREEN)) }
					{ drainAnimationRow !== -1 ? 
						Display.renderDrainingAnimation(filledPolygons, drainAnimationRow) : 
						filledPolygons.map(renderFilledPolyAtScale) 
					}
										{/* { filledPolygons.map(renderFilledPolyAtScale) } */}

					{ partiallyFilledArea && renderPartiallyFilledArea(partiallyFilledArea, scale) }
				</Layer>
			</Stage>
		);
	}
}

export default Display;

const renderLine = (color: FrenzyColor) => (line: FrenzyLine, lineIndex: number) => {
	// console.log(`Drawing line: ${points(line)} with scale ${scale}`)
	return (
		<Line 
			listening={false}
			draggable={false}
			perfectDrawEnabled={false}	
			hitStrokeWidth={0}
			dashEnabled={false}
			shadowEnabled={false}
			shadowForStrokeEnabled={false}
			lineCap='square'
			key={`line-${lineIndex}`}
			points={points(line)}
			stroke={color}
			strokeWidth={1}
		/>
	);
}

const renderFilledArea = (index: string, polygonLines: FrenzyLine[], strokeColor: FrenzyColor | "transparent", fillColor: FrenzyColor, extraOptions?:any) => {
	const flatPointsList = polygonLines.flatMap((l) => l.start);

	return <Line
		listening={false}
		draggable={false}
		perfectDrawEnabled={false}
		dashEnabled={false}
		shadowEnabled={false}
		shadowForStrokeEnabled={false}
		key={`filledArea-${index}`}
		points={flatPointsList}
		stroke={strokeColor}
		hitStrokeWidth={0}
		lineCap='square'
		closed
		strokeWidth={1}
		fill={fillColor}
		{...extraOptions}
	/>
}

const renderPartiallyFilledArea = (pf: PixelField, scale: number) => {
	const { sparseArray, color } = pf;
	// console.time(`renderPFA`)

	const polys = toOptimalPolyArray(sparseArray);

	const pointMap = polys.map((p,i) => {
		return <Line 
			listening={false}
			draggable={false}
			perfectDrawEnabled={false} 
			dashEnabled={false}
			shadowEnabled={false}
			shadowForStrokeEnabled={false}
			key={`pfa-poly-${i}`}
			points={p}
			strokeWidth={1}
			hitStrokeWidth={0}
			lineCap='square'
			stroke={color}
			closed
			fill={color}
		/>
	})
	// console.timeEnd(`renderPFA`);
	return pointMap;
}

export const renderPartiallyFilledAreaInefficientPixelBased = (pf: PixelField, scale: number) => {
	const { sparseArray, color } = pf;
	console.time(`renderPFA`)

	const points = sparseArrayToPointsArray(sparseArray);

	const pointMap = points.map((p) => {
		return <Rect 
			listening={false}
			draggable={false}
			perfectDrawEnabled={false} 
			dashEnabled={false}
			shadowEnabled={false}
			shadowForStrokeEnabled={false}
			key={`pfa-${p[0]}-${p[1]}`}
			x={p[0]}
			y={p[1]} 
			width={1}
			height={1} 
			strokeWidth={0}
			strokeScaleEnabled={false}
			hitStrokeWidth={0}
			lineCap='square'
			fill={color}
		/>
	})
	console.timeEnd(`renderPFA`);
	return pointMap;
}

